import { Formik } from 'formik';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import {
  ActionButton,
  Buttons,
  CancelButton,
  Checkbox,
  Confirmation,
  DeleteButton,
  Drawer,
  Field,
  Form,
  FormMessage,
  Icon,
  ProjectTaskTagMultiSelect,
  Radio,
  Tab,
  Tabs,
  TextInput,
} from '~/components';
import ReadTextbox from '~/components/read-only/ReadTextbox';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useCurrencyFormat, useDirtyCheck, useDocumentTitle, useForm } from '~/hooks';
import { colors } from '~/styles';
import { dateFormats, emptyStringToNull, mergeValues } from '~/utils';
import CapFeesCheckbox from './CapFeesCheckbox';
import ProjectTaskHistory from './ProjectTaskHistory';
import ProjectTaskMemberMultiSelect from './ProjectTaskMemberMultiSelect';
import ProjectTaskMembersTable from './ProjectTaskMembersTable';
import ProjectTaskRelationships from './ProjectTaskRelationships';
import ProjectTaskRolesTable from './ProjectTaskRolesTable';
import ShiftBlockedTasksConfirmation from './ShiftBlockedTasksConfirmation';
import moment from 'moment';

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-top: 1.625rem;
  margin-bottom: 1.625rem;
`;

const ProjectTaskFormContext = React.createContext({ forms: [] });

function ProjectTaskForm({ task, project, onSaved, onClose, onDelete, onSaveAndNew, isNew = false }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [{ isSubmitting, status, message, saved }, form] = useForm();
  const [hasDependencies, setHasDependencies] = useState(true);
  const confirmation = useConfirmation();
  const currencyFormat = useCurrencyFormat({ currency: project.currency });
  const [tabIndex, setTabIndex] = useState(0);
  const toast = useToast();

  const [forms, setForms] = useState([]);

  useEffect(() => {
    (async () => {
      if (!task?.id) return;

      const { data } = await api.www.workspaces(workspace.id).projects(project.id).tasks(task.id).hasDependencies();

      setHasDependencies(data);
    })();
  }, [api, workspace.id, project.id, task]);

  const title = 'Edit Task';

  useDocumentTitle(title);

  const formRef = useRef();
  const firstFieldRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty || forms.length > 0);

  const defaultValues = {
    code: '',
    description: '',
    end: null,
    isBillable: true,
    name: '',
    estimatedHours: '',
    estimatedFees: '',
    lockTime: false,
    requireNotes: false,
    forAssignedOnly: false,
    taskCategory: null,
    start: null,
    statusId: 'not_started',
    roles: [],
    members: [],
    capHours: false,
    capAssignedHours: false,
    capFees: false,
    tags: [],
    parent: null,
    predecessors: [],
    successors: [],
  };

  const initialValues = mergeValues(defaultValues, task);

  const tabErrors = {
    task: ['code', 'description', 'end', 'isBillable', 'name', 'estimatedHours', 'estimatedFees', 'start', 'statusId'],
  };

  return (
    <ProjectTaskFormContext.Provider value={{ forms, setForms }}>
      <Drawer
        title={title}
        isOpen
        onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
        onClose={onClose}>
        {(closeDrawer) => {
          const handleCloseClick = () => dirtyCheck(() => closeDrawer());

          async function handleSubmit(values) {
            try {
              let shiftSuccessors = true;
              const endChanged = task.end && values.end && task.end !== values.end;
              if (project.useDependencies && endChanged) {
                const { data: blockedTasks } = await api.www
                  .workspaces(workspace.id)
                  .projects(project.id)
                  .tasks(task.id)
                  .scheduledSuccessors({ successorIds: values.successors.map((s) => s.id) });
                if (blockedTasks.length > 0) {
                  const result = await confirmation.prompt((resolve) => (
                    <ShiftBlockedTasksConfirmation taskName={task.name} blockedTasks={blockedTasks} resolve={resolve} />
                  ));
                  if (!result) return;
                  shiftSuccessors = result.shiftSuccessors;
                }
              }

              const completingTask = values.statusId === 'completed' && task.statusId !== 'completed';
              // Use the staged predecessors — the Relationships editor can change them in this same save.
              const hasOpenPredecessors = values.predecessors?.some((p) => p.statusId !== 'completed');
              if (project.useDependencies && completingTask && hasOpenPredecessors) {
                const confirmed = await confirmation.prompt((resolve) => (
                  <Confirmation resolve={resolve}>
                    This task is blocked by tasks that haven't been completed yet. Are you sure you want to mark it as
                    completed?
                  </Confirmation>
                ));
                if (!confirmed) return;
              }

              form.submit(formRef.current.status.action);
              const body = emptyStringToNull({
                ..._.omit(values, ['tags', 'parent', 'taskCategory', 'predecessors', 'successors']),
                roles: values.roles.map(({ projectRoleId, hours }) => ({
                  projectId: project.id,
                  projectRoleId,
                  hours,
                })),
                members: values.members.map(({ projectMemberId, hours }) => ({
                  projectId: project.id,
                  projectMemberId,
                  hours,
                })),
                projectTaskTagAssignments: values.tags.map((tag) => ({ projectTaskTagId: tag.id })),
                predecessors: values.predecessors.map((p) => ({ predecessorId: p.id })),
                successors: values.successors.map((s) => ({ successorId: s.id })),
                parentId: values.parent?.id ?? null,
                projectTaskCategoryId: values.taskCategory?.id ?? null,
                shiftSuccessors,
              });

              const { data } = await api.www
                .workspaces(workspace.id)
                .projects(project ? project.id : undefined)
                .tasks(task.id)
                .upsert(body);

              if (onSaved) {
                await onSaved(data);
              }

              toast.success('Task saved successfully');

              form.save(formRef.current.status.action);

              switch (formRef.current.status.action) {
                case 'new':
                  await onSaveAndNew();
                  break;

                default:
                  closeDrawer();
                  break;
              }
            } catch ({ message }) {
              form.error({ message });
            }
          }

          async function handleDelete() {
            await onDelete(task);
            closeDrawer();
          }

          return (
            <Formik
              innerRef={formRef}
              initialValues={initialValues}
              initialStatus={{ next: 'done' }}
              onSubmit={handleSubmit}
              validateOnBlur={false}
              validateOnChange={false}
              validationSchema={Yup.object().shape({
                code: Yup.string().label('Task Code').max(255),
                description: Yup.string().label('Notes').max(5000),
                end: Yup.date()
                  .label('End Date')
                  .nullable()
                  .when('start', (start, schema) =>
                    start ? schema.min(Yup.ref('start'), 'End Date must be after Start Date') : schema,
                  ),
                isBillable: Yup.bool(),
                name: Yup.string().label('Task Name').max(255).required(),
                estimatedHours: Yup.number().label('Hours Budget').min(0).max(999999.99).nullable(),
                estimatedFees: Yup.number()
                  .label('Services Revenue Budget')
                  .min(-9999999999.99)
                  .max(9999999999.99)
                  .nullable(),
                start: Yup.date().label('Start Date').nullable(),
                statusId: Yup.string().label('Status').required(),
              })}>
              {({ errors, values, values: { roles, members }, setFieldValue, submitForm, setStatus, setValues }) => {
                const submit = async (action) => {
                  // Submit all open inline forms
                  for await (const form of forms) {
                    await form.ref.current.submitForm();
                  }

                  // If at least one form is invalid, don't submit the drawer
                  for await (const form of forms) {
                    if (form.ref.current && !form.ref.current.isValid) return;
                  }

                  setStatus({ action });
                  submitForm();
                };

                const handleStartChange = (value) => {
                  const oldStart = values.start;
                  const oldEnd = values.end;

                  setFieldValue('start', value);

                  if (value && oldStart && oldEnd && moment(oldEnd).isSameOrAfter(oldStart)) {
                    const diff = moment(value).diff(moment(oldStart), 'days');
                    const newEnd = moment(oldEnd).add(diff, 'days').format(dateFormats.isoDate);
                    setFieldValue('end', newEnd);
                  } else if (value && (!oldEnd || moment(value).isAfter(oldEnd))) {
                    setFieldValue('end', moment(value).format(dateFormats.isoDate));
                  }
                };

                return (
                  <Form>
                    <Tabs selectedIndex={tabIndex} onChange={(index) => setTabIndex(index)}>
                      <Tab>
                        Task
                        {tabErrors.task.some((key) => !!errors[key]) && (
                          <Icon icon="exclamation-circle" color={colors.danger} spaceLeft />
                        )}
                      </Tab>
                      {task.permissions.manage && <Tab>History</Tab>}
                    </Tabs>
                    <Content>
                      {status && <FormMessage.Error>{message}</FormMessage.Error>}

                      {
                        [
                          <>
                            <Form.Section title="Basic Information">
                              <Form.Control>
                                <Field.Text ref={firstFieldRef} name="name" placeholder="Task Name" maxLength={255} />
                              </Form.Control>
                              <Form.Control>
                                <Field.ProjectTaskCategorySelect
                                  name="taskCategory"
                                  placeholder="Standard Task Category"
                                  type="standard"
                                  clearable={false}
                                />
                              </Form.Control>
                              <Form.Control>
                                <Field.ProjectTaskStatusSelect name="statusId" placeholder="Status" />
                              </Form.Control>
                              <Form.Control>
                                <Field.ProjectTaskSelect
                                  name="parent"
                                  placeholder="Parent Task"
                                  project={project}
                                  disableMilestones
                                  exclude={[task.id]}
                                />
                              </Form.Control>
                              <Form.Control>
                                {project.useRoles && roles.length > 0 ? (
                                  <TextInput
                                    placeholder="Hours Budget"
                                    disabled
                                    value={roles.reduce((a, v) => a + v.hours, 0)}
                                  />
                                ) : !project.useRoles && members.length > 0 ? (
                                  <TextInput
                                    placeholder="Hours Budget"
                                    disabled
                                    value={members.reduce((a, v) => a + v.hours, 0)}
                                  />
                                ) : (
                                  <Field.Number
                                    name="estimatedHours"
                                    placeholder="Hours Budget"
                                    min={0}
                                    precision={2}
                                  />
                                )}

                                {!project.isBillable || !values.isBillable ? null : project.useRoles &&
                                  roles.length > 0 ? (
                                  <ReadTextbox
                                    label="Services Revenue Budget"
                                    value={currencyFormat.format(
                                      roles.reduce((a, v) => a + v.role.actualRate * v.hours, 0),
                                    )}
                                  />
                                ) : !project.useRoles && members.length > 0 ? (
                                  <ReadTextbox
                                    label="Services Revenue Budget"
                                    value={currencyFormat.format(
                                      members.reduce((a, v) => a + v.projectMember.actualRate * v.hours, 0),
                                    )}
                                  />
                                ) : (
                                  <Field.Currency
                                    name="estimatedFees"
                                    currency={project.currency}
                                    placeholder="Services Revenue Budget"
                                  />
                                )}
                              </Form.Control>

                              <Form.Control>
                                <Field.DayPicker
                                  name="start"
                                  onChange={(value) => handleStartChange(value)}
                                  placeholder="Start Date"
                                />
                                <Field.DayPicker name="end" placeholder="End Date" />
                              </Form.Control>
                              {project.isBillable && (
                                <Form.Control>
                                  <Field.RadioGroup name="isBillable">
                                    <Radio value={true} label="Billable" />
                                    <Radio value={false} label="Non-billable" />
                                  </Field.RadioGroup>
                                </Form.Control>
                              )}
                              <Form.Control>
                                <Field.Text name="code" placeholder="Task Code" maxLength={255} />
                              </Form.Control>
                              <Form.Control>
                                <Field.Control>
                                  <ProjectTaskTagMultiSelect
                                    name="tags"
                                    placeholder="Tags"
                                    value={values.tags}
                                    allowNew
                                    onChange={({ target: { value } }) => setFieldValue('tags', value)}
                                  />
                                </Field.Control>
                              </Form.Control>
                              <Form.Control>
                                <Field.TextArea name="description" placeholder="Notes" rows={4} maxLength={5000} />
                              </Form.Control>
                            </Form.Section>
                            {project.useDependencies &&
                              (values.predecessors?.length > 0 ||
                                values.successors?.length > 0 ||
                                task.permissions.manage) && (
                                <Form.Section title="Relationships">
                                  <ProjectTaskRelationships
                                    project={project}
                                    taskId={task.id}
                                    predecessors={values.predecessors}
                                    successors={values.successors}
                                    disabled={!task.permissions.manage}
                                    onChange={({ predecessors, successors }) =>
                                      setValues({ ...values, predecessors, successors })
                                    }
                                  />
                                </Form.Section>
                              )}
                            <Form.Section title="Settings">
                              <Form.Control>
                                <Field.Checkbox name="lockTime" label="Lock time for this task" />
                              </Form.Control>
                              <Form.Control>
                                <Field.Checkbox
                                  name="capHours"
                                  label="Cap the hours to the total budgeted for this task"
                                  disabled={
                                    values.capAssignedHours &&
                                    (project?.useRoles ? roles.length > 0 : members.length > 0)
                                  }
                                />
                              </Form.Control>
                              {project.isBillable && (
                                <Form.Control>
                                  <CapFeesCheckbox project={project} />
                                </Form.Control>
                              )}
                              <Form.Control>
                                <Field.Checkbox
                                  name="forAssignedOnly"
                                  label="Only assigned members and roles can track time to this task"
                                />
                              </Form.Control>
                              <Form.Control>
                                {project?.requireNotes && (
                                  <Field.Checkbox
                                    name="projectOverrideRequireNotes"
                                    label="Require notes on time entries for this task"
                                    disabled
                                    checked
                                  />
                                )}
                                {!project?.requireNotes && (
                                  <Field.Checkbox
                                    name="requireNotes"
                                    label="Require notes on time entries for this task"
                                  />
                                )}
                              </Form.Control>
                            </Form.Section>
                            {project.useRoles ? (
                              <>
                                <Form.Section title="Role assignment &amp; Budget">
                                  <ProjectTaskRolesTable
                                    project={project}
                                    task={values}
                                    value={roles}
                                    onChange={(value) => {
                                      if (_.isEmpty(value)) {
                                        setValues({ ...values, roles: value, capAssignedHours: false });
                                      } else {
                                        if (values.capAssignedHours === true) {
                                          setValues({ ...values, roles: value, capHours: true });
                                        } else {
                                          setFieldValue('roles', value);
                                        }
                                      }
                                    }}
                                  />
                                  <Form.Control>
                                    {roles.length > 0 ? (
                                      <Field.Checkbox
                                        name="capAssignedHours"
                                        label="Cap the hours by role"
                                        onChange={({ target: { checked } }) => {
                                          if (checked) {
                                            setValues({ ...values, capAssignedHours: true, capHours: true });
                                          } else {
                                            setFieldValue('capAssignedHours', false);
                                          }
                                        }}
                                      />
                                    ) : (
                                      <Checkbox label="Cap the hours by role" checked={false} disabled={true} />
                                    )}
                                  </Form.Control>
                                </Form.Section>
                                <Form.Section title="Member assignment">
                                  <Form.Control data-testid="members_form">
                                    <Field.Control>
                                      <ProjectTaskMemberMultiSelect
                                        value={members}
                                        onChange={({ target: { value } }) => setFieldValue('members', value)}
                                        project={project}
                                        initialValue={task.members}
                                        position="top"
                                      />
                                    </Field.Control>
                                  </Form.Control>
                                </Form.Section>
                              </>
                            ) : (
                              <Form.Section title="Member assignment &amp; Budget">
                                <ProjectTaskMembersTable
                                  project={project}
                                  task={values}
                                  value={members}
                                  onChange={(value) => {
                                    if (_.isEmpty(value)) {
                                      setValues({ ...values, members: value, capAssignedHours: false });
                                    } else {
                                      if (values.capAssignedHours === true) {
                                        setValues({ ...values, members: value, capHours: true });
                                      } else {
                                        setFieldValue('members', value);
                                      }
                                    }
                                  }}
                                />
                                <Form.Control>
                                  {members.length > 0 ? (
                                    <Field.Checkbox
                                      name="capAssignedHours"
                                      label="Cap the hours by member"
                                      onChange={({ target: { checked } }) => {
                                        if (checked) {
                                          setValues({ ...values, capAssignedHours: true, capHours: true });
                                        } else {
                                          setFieldValue('capAssignedHours', false);
                                        }
                                      }}
                                    />
                                  ) : (
                                    <Checkbox label="Cap the hours by member" checked={false} disabled={true} />
                                  )}
                                </Form.Control>
                              </Form.Section>
                            )}
                          </>,
                          <>
                            <ProjectTaskHistory projectId={project.id} taskId={task.id} />
                          </>,
                        ][tabIndex]
                      }
                    </Content>

                    <Drawer.Actions>
                      {task.id && task.permissions.manage && _.isFunction(onDelete) && (
                        <DeleteButton disabled={hasDependencies} onClick={handleDelete}>
                          Delete
                        </DeleteButton>
                      )}

                      <Buttons align="right">
                        <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                        {isNew && (
                          <ActionButton
                            isOutline
                            isLoading={isSubmitting === 'new'}
                            ok={saved === 'new'}
                            onClick={() => submit('new')}>
                            Save &amp; New
                          </ActionButton>
                        )}

                        <ActionButton
                          isLoading={isSubmitting === 'close'}
                          ok={saved === 'close'}
                          onClick={() => submit('close')}>
                          Save &amp; Close
                        </ActionButton>
                      </Buttons>
                    </Drawer.Actions>
                  </Form>
                );
              }}
            </Formik>
          );
        }}
      </Drawer>
    </ProjectTaskFormContext.Provider>
  );
}

export { ProjectTaskFormContext };
export default ProjectTaskForm;
